import { useCallback } from "react";
import type { InviteContext } from "@/components/dialogs/InviteDialog";

interface ChannelLite {
  type?: string | null;
  name?: string | null;
}

interface RenderUserLite {
  display_name: string;
}

interface UseDmInviteHandlerArgs {
  channel: ChannelLite | null | undefined;
  channelId: string | null;
  user: { id: string } | null | undefined;
  channelMemberList: readonly { user_id: string }[];
  renderUserMap: ReadonlyMap<string, RenderUserLite>;
  setDmInviteContext: (ctx: InviteContext | null) => void;
}

/**
 * Builds the `onInviteToConversation` handler the header passes to
 * trigger the DM / group-DM invite flow.
 *
 * - For 1:1 DMs: looks up the "other" channel member and opens
 *   `InviteDialog` in `dm` mode with their id + name.
 * - For group DMs: opens the dialog in `group_dm` mode with the
 *   channel id + name.
 * - For everything else: a no-op (the header should not be calling
 *   it on regular channels, but we don't crash if it does).
 *
 * Stable identity across renders so the header's `memo` doesn't bust
 * unless one of its dependencies actually changes.
 */
export function useDmInviteHandler({
  channel,
  channelId,
  user,
  channelMemberList,
  renderUserMap,
  setDmInviteContext,
}: UseDmInviteHandlerArgs) {
  return useCallback(() => {
    if (channel?.type === "dm") {
      const otherMember = channelMemberList.find((m) => m.user_id !== user?.id);
      if (!otherMember) return;
      const otherUser = renderUserMap.get(otherMember.user_id);
      setDmInviteContext({
        type: "dm",
        otherUserId: otherMember.user_id,
        otherUserName: otherUser?.display_name || channel?.name || "User",
      });
    } else if (channel?.type === "group_dm" && channelId) {
      setDmInviteContext({
        type: "group_dm",
        channelId,
        channelName: channel?.name ?? undefined,
      });
    }
  }, [
    channel,
    channelId,
    user,
    channelMemberList,
    renderUserMap,
    setDmInviteContext,
  ]);
}
